import { z } from 'zod';
import * as t from 'Shared/model/basic';


export const generalSettings = z.object({
	enabled: z.boolean(),
	timezone: z.string(),
	dayStart: t.time,
	dayEnd: t.time,
	testNow: t.date.optional(),
	enableMenus: z.boolean(),
	enableSpecials: z.boolean(),
	enableEvents: z.boolean(),
	enableStaffResources: z.boolean(),
});
export type GeneralSettings = z.infer<typeof generalSettings>;


/*
	The venue's colours.

	'background', 'text', 'feature' and 'white' are the standard colours: every venue has them, they
	can't be removed, and the frontend components' base css plus a handful of standard styles read them by name.
	They start out at the defaults in defaultPalette / addVenue.ts. 'white' is always plain white - it
	is here so a style that wants white text on a filled surface can name a palette entry rather than
	carrying a literal, and stays editable if a venue ever wants an off-white.

	Everything else is an additional colour the venue chose to add on /admin/settings - 'dimBackground'
	(a filled surface a shade off the page, used by menu cards and the sub-nav button; it falls back to
	'background' where a venue has none - see dimBackground() in settingColour.ts), 'inverseText',
	'feature2', 'feature3' and the 'food'/'drinks'/'cactus' holdovers are the names sites have used so
	far. They come and go per site (see migration 56's movePalettes.ts, which seeds each site only the
	extra colours its old code actually referenced), so the catchall keeps the type open and callers
	treat a missing entry the same as an empty one (see resolveColour()).
*/
export const palette = z.object({
	background:    z.string(),
	text:          z.string(),
	feature:       z.string(),
	white:         z.string(),
}).catchall(z.string());
export type Palette = z.infer<typeof palette>;

/* The colours every venue must define, in the order they make sense to look at. */
export const corePaletteFields: readonly string[] = ['background','text','feature','white'];


/*
	A colour anywhere in the frontend settings.

	It names a palette entry rather than carrying a colour, so that editing the palette moves
	everything pointing at it. That is how every nav colour was written when they lived in code, and
	the whole reason for storing a link rather than a value.

	A slot the venue never styled has no SettingColour at all - the field is left out of the DB
	document rather than carrying a 'none' variant.

	'brightnessAdjustment' shifts the linked palette colour's HSL saturation and lightness by that many
	points - positive to lift, negative to darken. Only palette links carry it (a custom colour is
	already the exact value the venue wants); omitted when 0.
*/
export const settingColour = z.union([
	z.object({palette: z.string(), brightnessAdjustment: z.number().optional()}),
	z.object({literal: z.string()}),
]);
export type SettingColour = z.infer<typeof settingColour>;


/*
	A fluid css length: a clamp() that grows with the container width. Stored either as a plain css
	string ('40px', '2em') or as the {type:'fluid',...} object the Amount control writes -
	Shared/frontend/widgets/Amount.amount() turns that into a clamp() via fluidType(). pageMin/pageMax
	are the container widths the size ramps between; minValue/maxValue the px sizes at those widths.
*/
export const fluidLength = z.object({
	type:     z.literal('fluid'),
	pageMin:  z.number(),
	minValue: z.number(),
	pageMax:  z.number(),
	maxValue: z.number(),
});
export type FluidLength = z.infer<typeof fluidLength>;


/*
	One standard style the venue has turned on for a page, named by the category and type it sits under
	in siteTheme().styles. 'params' carries the values for a style that takes them. This is the same
	shape the page and menu documents store their styles in, and what the frontend's resolveStyleDetails()
	consumes - see StylesField / Shared/frontend/widgets/Styles.
*/
export const styleTag = z.object({
	category: z.string(),
	type:     z.string(),
	params:   z.record(z.string(), z.any()).optional(),
});
export type StyleTag = z.infer<typeof styleTag>;


/*
	One css property set by a Style Builder rule.

	'property' names an entry in ruleProperties() and 'params' carries the values that entry's editor
	writes - the same shape a styleTag's params has. It stays a plain string for the same reason
	styleTag.type does: the catalogue lives in code, and a name withdrawn from it must not fail the
	parse of every document that still mentions it. venueCustomStyles() skips one it can't find.
*/
export const customStyleProperty = z.object({
	property: z.string(),
	params:   z.record(z.string(), z.any()).optional(),
});
export type CustomStyleProperty = z.infer<typeof customStyleProperty>;


/*
	One step of a 'path' location - a component somewhere below the previous step, optionally
	filtered by a class. 'relation' says how far below: a direct child, or any descendant. An empty
	'aClass' matches any component, so the four steps LocationBuilder offers are the four
	combinations. pathLocation() in Shared/frontend/widgets/locationMatches.ts turns these into the
	location expression the matcher reads.

	Stored structured rather than as the expression's own tokens so that no escaped class name
	reaches the database and nothing has to parse the grammar back out.
*/
export const locationPathStep = z.object({
	relation: z.enum(['child','descendant']),
	aClass:   z.string(),
});
export type LocationPathStep = z.infer<typeof locationPathStep>;


/*
	One rule inside a Style Builder style: where it applies, and the css properties it sets there.
	Properties that apply at different places are different rules.

	'location' is the Style Builder's own idea of location, deliberately separate from the location
	expressions the code-defined styles carry - see ruleLocation() in
	Shared/frontend/styles/ruleProperties.ts, which maps it onto one. 'self' names the styled
	component itself; 'class' instead names a class, and then 'locationClass' says which one -
	anything at or below the styled component carrying it. 'path' is the general case the other two
	are shorthands for, and 'locationPath' spells it out step by step - a single 'child' or
	'descendant' step with no class is what a depth-only choice like "all children" or "all
	descendants" now has to be built as.

	'locationClass' belongs to the 'class' location and 'locationPath' to 'path'; each is written
	only for its own: LocationBuilder picks the location and the class in the one click, so 'class'
	never stands without one, and a path that has had no steps added yet is the styled component
	itself.

	There is always at least one property: the Style Builder seeds one when the rule is created and
	won't let the last one be removed.
*/
export const customStyleRule = z.object({
	location:      z.enum(['self','class','path']),
	locationClass: z.string().optional(),
	locationPath:  z.array(locationPathStep).optional(),
	properties:    z.array(customStyleProperty),
});
export type CustomStyleRule = z.infer<typeof customStyleRule>;


/*
	A style the venue built for itself in the Style Builder (/admin/style-builder). standardStyles()
	exposes these as the whole 'Venue styles' category, so they appear in every style picker - every
	per-venue style is built here now, none are left in code.

	'explanation' is the same field the code-defined entries set - the tooltip StylesField shows on
	hover. There is always at least one rule: the Style Builder seeds one when the style is created
	and won't let the last one be removed.

	'extends' names another style of this venue's that this one inherits the rules of, and its own
	rules are added after those, so they win where the two set the same property. Only the venue's own
	styles can be extended: a code-defined style is a shape the Style Builder can't express, so
	inheriting one would produce a style the venue could see but not edit. It is a bare name rather
	than a {category,type} tag because the category is always 'Venue styles' and names are unique
	within it; venueCustomStyles() resolves it. A style that extends nothing leaves the field off.

	Every style resolves against the style path from whichever picker turns it on. There is no nav
	variant any more: the nav's own colours and fonts are plain fields on venue.settings.frontend.nav
	(see navSettings below), not something a venue builds a style for.
*/
export const customStyle = z.object({
	name:        z.string(),
	explanation: z.string().optional(),
	extends:     z.string().optional(),
	rules:       z.array(customStyleRule),
});
export type CustomStyle = z.infer<typeof customStyle>;


/*
	One entry in the venue's nav.

	An item points at a venue page by its slug or at a menu by the slug the menu designer gave it -
	never at a raw path. Every nav path the settings classes used to carry was one or the other: '/',
	'/events', '/hire' and '/contact' are pages, '/menu', '/food', '/drinks', '/drink' and '/dine' are
	menus. navItemLink() in MobileFooterNav.tsx turns either into a url, and a page the venue has
	disabled - or a menu it has deleted - resolves to nothing, so the item doesn't light up and its
	link goes nowhere.

	An item with children and no target of its own is a heading that only opens them; that is the one
	case where both are absent.
*/
export const navLink = z.object({
	label: z.string(),
	page:  z.string().optional(),
	menu:  z.string().optional(),
});
export type NavLink = z.infer<typeof navLink>;

/*
	The desktop bar's own list. Labels only - no icons - and one level of nesting: a child list is
	drawn as the drop-down under its button (see SubNav.tsx). A child of a child would have nowhere
	to go.
*/
export const desktopNavItem = navLink.extend({
	items: z.array(navLink).optional(),
});
export type DesktopNavItem = z.infer<typeof desktopNavItem>;

/*
	The mobile footer's own list, kept apart from the desktop one on purpose. A phone shows a handful
	of buttons and the rest go behind 'More', so which entries earn a place - and in what order - is a
	different question from what the desktop bar has room for. The Drummer is the example: its footer
	carries Contact and leaves Private Event to the 'More' panel, where its desktop bar has both.

	'icon' is a key into navIcons (see navIcons.ts), drawn above the label. Flat: there is no room to
	open a drop-down under a footer button.
*/
export const mobileNavItem = navLink.extend({
	icon: z.string(),
});
export type MobileNavItem = z.infer<typeof mobileNavItem>;


/*
	The nav settings - what each nav holds, and how it looks.

	The desktop bar and the mobile footer carry a list each, because they answer different questions:
	see mobileNavItem. The 'More' panel has no list of its own - it draws the desktop one, flattening
	its drop-downs, so that everything the site has is reachable from a phone even though the footer
	only has room for a few of them.

	The footer shows every item in its list, and 'includeMoreButton' says whether a 'More' button
	follows them.

	Each of the three nav nodes - the desktop bar, the mobile footer buttons, and the mobile 'More'
	full-screen menu - also carries a 'rules' list and a 'styles' list.

	'rules' is how the venue pulls away from the nav components' palette-driven defaults (see navCss.ts). They are
	Style Builder rules, the same customStyleRule shape the venue's own styles are made of: each names
	a part of that node - its button, a hovered button, the logo (see settingsParts in
	ruleProperties.ts) - and sets properties from the same catalogue there. settingRulesCss() turns a
	node's rules into one css object, standardStyles() carries that as the 'Settings rules' category, and
	ShowWrapElement resolves it onto the node's wrapper. A part the venue never names is left off and
	nav()'s own value stands.

	They were typed fields per part until 2026-09-08 (navButtonSettings and the rest, turned into css
	by Shared/model/settingNav.ts). A part list that has to be spelled out three times over - in the
	schema, in the settings tree and in nav() - is a part list nobody adds to, and the venue could
	only ever set the handful of properties somebody had thought to type.

	'styles' is the standard styles turned on for that node ('Hide on desktop', 'Fade in background' -
	see navStyles.ts), layered on top of what nav() builds. initPageStyles() folds the three lists in
	alongside the site-wide styles so they reach the nav on every page, and ShowWrapElement resolves
	them at ['nav','desktop'], ['nav','mobile'] and ['nav','more'] - the same three paths the rules
	land on.
*/
export const desktopNavSettings = z.object({
	items:  z.array(desktopNavItem),
	styles: z.array(styleTag).optional(),
	rules:  z.array(customStyleRule),
});
export type DesktopNavSettings = z.infer<typeof desktopNavSettings>;

/* The full-screen menu behind the mobile nav's 'More' button. Its current-page button is the mobile
   nav's (NavMore uses mobileNavActiveCss()), so its 'current' part dresses that. */
export const moreNavSettings = z.object({
	styles: z.array(styleTag).optional(),
	rules:  z.array(customStyleRule),
});
export type MoreNavSettings = z.infer<typeof moreNavSettings>;

/* How many buttons fit across the bottom of a phone, the 'More' button included. */
export const maxMobileNavButtons = 6;

export const mobileNavSettings = z.object({
	items:             z.array(mobileNavItem),
	includeMoreButton: z.boolean(),
	styles:            z.array(styleTag).optional(),
	rules:             z.array(customStyleRule),
	more:              moreNavSettings,
});
export type MobileNavSettings = z.infer<typeof mobileNavSettings>;

export const navSettings = z.object({
	desktop: desktopNavSettings,
	mobile:  mobileNavSettings,
});
export type NavSettings = z.infer<typeof navSettings>;


/*
	The styling for one named piece of frontend text - a page title, a teaser. Every field is
	optional: a venue records only what it wants to pull away from the defaults the code provides.
	'color' names a palette entry, as colours do everywhere in these settings; the rest are the plain
	css values the venue settings classes used to hard-code. See buildSiteTheme(), which merges
	this onto the base style via textStyleRule().

	The Text section of /admin/settings edits these as an additive list - a row per property the
	document carries, added and removed by the venue - so a field being here at all is the venue
	saying it means something. A property carrying the value standardText() gives it anyway should be
	left out rather than written (see seedTextStyles.ts, which drops those as it seeds).
*/
export const textStyle = z.object({
	color:         settingColour.optional(),
	fontFamily:    z.string().optional(),
	fontWeight:    z.number().optional(),
	fontStyle:     z.string().optional(),
	fontSize:      z.union([z.string(), fluidLength]).optional(),
	textTransform: z.string().optional(),
	lineHeight:    z.number().optional(),
	letterSpacing: z.number().optional(),
	textAlign:     z.string().optional(),
	margin:        z.string().optional(),
});
export type TextStyle = z.infer<typeof textStyle>;


/*
	Everything the event page and the backend events screen read off the venue document beyond the
	shared settings. 'styles' are the standard styles applied to every event page, layered on top of
	the site-wide styles. The date and name headings default to the venue 'Page title' text style
	(see EventPage.tsx); a venue whose event headings depart from it turns on a style through
	that same 'styles' list - 'Text > Convert to' for another text type's look, a Style Builder
	style for anything more.

	'rules' is the same Style Builder rule list the nav nodes carry (see navSettings). Its one part is
	the date and name headings; settingsNodeEntry() in standardStyles.ts carries it as a
	'Settings rules' category entry whose location lands it on the <h3>s (and misses the lineup artist
	names), the same way the nav's rules reach the nav wrapper. Edited under 'Event page' on
	/admin/settings, below the Styles picker.

	'defaultStart' is the time of day createEvent() (in shared/backend/eventsPage/EventsPage.tsx)
	pre-fills a freshly-created one-off event with - base
	noon, Family raised it to 7pm before this moved onto the document. See
	venue.settings.frontend.event, edited under 'Frontend settings' on /admin/settings.
*/
export const eventSettings = z.object({
	styles:       z.array(styleTag).optional(),
	rules:        z.array(customStyleRule),
	defaultStart: t.time,
});
export type EventSettings = z.infer<typeof eventSettings>;


/* The names of the two styles a venue is created with - what frontendSettings.buttonStyle and
   subNavButtonStyle name until the venue points them somewhere else. See defaultCustomStyles, which
   builds them. */
export const standardButtonStyleName = 'Standard Button';
export const subNavButtonStyleName = 'Sub Nav Button';


/*
	The footer - the divider line above it and the venue-name heading.

	'styles' is the standard styles turned on for it, layered on top of what BaseFooter.tsx
	builds. 'rules' is the same Style Builder rule list the nav nodes carry (see navSettings): its
	parts are the footer bar itself (divider + padding) and the venue-name heading, and
	settingsNodeEntry() in standardStyles.ts carries it as a 'Settings rules' category entry.

	Both are folded in by initPageStyles() alongside the site-wide styles and resolved by
	ShowWrapElement at ['footer'] - the same location the 'Hide footer' style uses. The divider and
	heading colours a venue used to set here as a flat object now come from BaseFooter.tsx's
	own palette-driven default (feature divider, text heading).
*/
export const footerSettings = z.object({
	styles: z.array(styleTag).optional(),
	rules:  z.array(customStyleRule),
});
export type FooterSettings = z.infer<typeof footerSettings>;


/*
	'bodyFontFamily' is the page's own font - the one rootCss() hands down, that anything not given
	a font of its own is drawn in: a footer address, a menu teaser, an event tagline, bare text in a
	block of embedded html, and every heading, button and nav that names no font of its own. It is
	also the family standardText() builds 'Plain text' and 'Tagline' from. Optional because a document
	only carries what the venue set; with nothing set the browser's sans-serif stands.

	The six styles below are the named pieces of text, each departing from standardText() on the
	properties the venue set.
*/
export const textSettings = z.object({
	bodyFontFamily: z.string().optional(),

	pageTitle: textStyle,
	title:     textStyle,
	teaser:    textStyle,
	tagline:   textStyle,
	subtitle:  textStyle,
	plain:     textStyle,
});
export type TextSettings = z.infer<typeof textSettings>;


/*
	There is no 'embeddedHtml' frontend setting any more. The base styling for every element inside a
	block of embedded / user-entered html - headings, paragraphs, links, images, bold - stays in
	embeddedHtmlCss() in pageCss.ts; the only piece a venue ever pulled away from was the
	<strong> font (The Townie, 'REM, sans-serif'), which is now an ordinary Style Builder style on the
	venue document ('Strong text' in venue.settings.frontend.customStyles, seeded by
	seedEmbeddedHtmlStyles.ts): a 'Font family' rule at the 'Bold text' location class, which resolves
	to the '.embedded-html strong' selector - see embeddedElements in
	Shared/frontend/styles/ruleProperties.ts. Turned on via the venue's site-wide styles list.
*/


/*
	The specials groups this venue runs, held as {key: label} in 'groups' - {general:'General'},
	{general:'General', beers:'Beers'} and the like. The key is what lands on special.type and in a
	{specials:...} widget's config; the label is what the specials page and the widget editor show.
	Edited under 'Specials' on /admin/settings (a map leaf of the settings tree), read back through
	specialOptions() in shared/backend/widgets/Content.tsx.

	A venue starts with {general:'General'} (addVenue.ts) - what BaseSettings used to hard-code - and
	must keep at least one group, since createSpecial() uses the first; the settings editor won't let
	the last one be removed. 'general' itself can be deleted once another group exists. The handful
	that also ran a beers group named {beers:'Beers'} in their siteTheme() before this moved onto the
	document.
*/
export const specialsSettings = z.object({
	groups: z.record(z.string(), z.string()),
});
export type SpecialsSettings = z.infer<typeof specialsSettings>;


/*
	The fonts a venue's pages load. Edited under 'Fonts' on /admin/settings; loaded into the page head
	by VenueFontLinks. Where each is used is said where the rest of that thing's look is: the body font
	and text styles under 'Text', a button's font in its Style Builder style, a nav's in its rules -
	each with a 'Font family' dropdown of these fonts.

	'google' are fonts.googleapis.com css2 family specs, eg 'Oswald:wght@200..700'.
	'external' come from any other font site: 'css' is what gets put in a <style> tag - an @import of
	the site's stylesheet or its @font-face rules.
	'uploaded' are font files in the venue's asset store, in the 'Fonts' folder - one face each, so
	a family with a regular and a bold is two entries under the one family name. 'weightMax' is set on
	a variable font only: the file covers weight to weightMax.

	'fallback' is the generic family a font's css font-family ends with, eg 'sans-serif'.
*/
export const googleFont = z.object({
	spec:     z.string(),
	fallback: z.string(),
});
export type GoogleFont = z.infer<typeof googleFont>;

export const externalFont = z.object({
	family:   z.string(),
	fallback: z.string(),
	css:      z.string(),
});
export type ExternalFont = z.infer<typeof externalFont>;

export const uploadedFontFormat = z.enum(['woff2','woff','ttf','otf']);
export type UploadedFontFormat = z.infer<typeof uploadedFontFormat>;

export const uploadedFont = z.object({
	family:   z.string(),
	fallback: z.string(),
	weight:   z.number(),
	weightMax: z.number().optional(),
	style:    z.enum(['normal','italic']),
	hash:     z.string(),
	format:   uploadedFontFormat,
	dev:      z.boolean().optional(),
});
export type UploadedFont = z.infer<typeof uploadedFont>;

export const venueFonts = z.object({
	google:   z.array(googleFont),
	external: z.array(externalFont),
	uploaded: z.array(uploadedFont),
});
export type VenueFonts = z.infer<typeof venueFonts>;

export const fontsAssetFolder = 'Fonts';


export const frontendSettings = z.object({
	jsEmbedCode: z.string(),
	palette: palette,
	/* The site-wide standard styles - the base layered under every page, that a page document's own
	   styles and venue.settings.frontend.event.styles then add to. Same {category,type,params} shape
	   as event.styles. Edited under 'Page styles' on /admin/settings; read by initPageStyles() and
	   the section editor's active-styles calc. */
	styles: z.array(styleTag).optional(),
	/*
		The venue styles (by name, from customStyles) the code's own buttons are dressed in site-wide -
		'Button style' and 'Subnav button style' under 'Page styles' on /admin/settings.

		'buttonStyle' dresses every call to action: a buttons widget's, a menu button row's, the
		mobile 'More' menu's. 'subNavButtonStyle' dresses the button rows under a page heading - the
		event page's lineup filters and Buy / Book, the menu's section jumps, a widget's sticky row -
		whose code base is a filled dim background at the smaller size of a sub-nav button.
		buttonStyleCss.ts flattens the named style's 'self' rules over the code's base, so a style
		that says nothing about a property leaves the base standing.

		A page or a Styles widget restyles some of its buttons by turning on a venue style with its
		'Apply to' set to 'Elements: Button' / 'Elements: Sub nav button', which replaces this one
		outright for those buttons. Bare names for the same reason a CustomStyle's 'extends' is: a name the venue has since deleted must not fail the
		parse, and namedStyleCss() warns instead. A rename in the Style Builder brings these along -
		see renameCustomStyle.ts. Absent means the code's base alone.
	*/
	buttonStyle: z.string().optional(),
	subNavButtonStyle: z.string().optional(),
	/* The venue's own styles, built in the Style Builder and stored here rather than in code. Folded
	   into the 'Venue styles' style category by standardStyles(). */
	customStyles: z.array(customStyle).optional(),
	/* The venue's own location classes - names it hangs off a component with the 'Classes' style and
	   then targets from a Style Builder rule's 'class' location. Folded into siteTheme().classes
	   alongside the code-defined ones. Edited under 'Page styles' on /admin/settings. */
	locationClasses: z.array(z.string()).optional(),
	nav: navSettings,
	event: eventSettings,
	footer: footerSettings,
	text: textSettings,
	specials: specialsSettings,
	fonts: venueFonts,
});
export type FrontendSettings = z.infer<typeof frontendSettings>;


/*
	One page built in the page builder. These used to be the pages() list on each venue's settings
	class, which is why the venue's own pages could only be added in code.

	'slug' is the page's key: it is the 'name' on the page document holding the page's content, the
	frontend route ('/'+slug) and the backend route ('/admin/page/'+slug), so renaming it moves the
	page document too - see renameVenuePageSlug(). The one page with 'home' set sits at '/' instead.

	'title' is the page's one name: the Pages menu's label, the builder's heading, and what PageMeta
	puts in the <title>. The code-defined pages carried a separate metaTitle for that last one, but
	of the pages migrated out of code only five ever differed, four of them by a stray capital.
*/
export const pageSettings = z.object({
	slug:    z.string(),
	title:   z.string(),
	home:    z.boolean(),
	disable: z.boolean(),
});
export type PageSettings = z.infer<typeof pageSettings>;


export const venueSettings = z.object({
	general:  generalSettings,
	frontend: frontendSettings,
	/* Beside 'frontend' rather than inside it: these drive the backend's routes as well. */
	pages:    z.array(pageSettings),
});
export type VenueSettings = z.infer<typeof venueSettings>;


/* The core colours a brand new site starts out with - see addVenue.ts, which seeds these. A new
   venue has no additional colours; it adds them on /admin/settings. */
export const defaultPalette: Palette = {
	background:    '#ffffff',
	text:          '#000000',
	feature:       '#292849',
	white:         '#ffffff',
};


function fromPalette(key:string): SettingColour
{
	return {palette:key};
}


/*
	The nav settings a brand new site starts out with - a desktop bar and a phone footer holding one
	item each, pointing at the home page defaultVenuePages gives it, and no rules on any node. Every colour and font the nav shows out of
	the box comes from the nav components' palette-driven defaults; a venue that wants to pull away
	adds rules under 'Styling' on /admin/nav. Each node is present with an empty list so the settings
	page always has somewhere to write.
*/
export const defaultNavSettings: NavSettings = {
	desktop: {items: [{label: 'Home', page: 'home'}], rules: []},
	mobile: {
		items:             [{label: 'Home', page: 'home', icon: 'home'}],
		includeMoreButton: true,
		rules:             [],
		more:              {rules: []},
	},
};


/*
	The event settings a brand new site starts out with - an empty style list and a noon default start
	for a new one-off event, as BaseSettings used to hard-code. With no style turned on, the date and
	name headings sit at the venue 'Page title' text style; a venue that wants them to depart from it
	adds a style under 'Event page' on /admin/settings.
*/
export const defaultEventSettings: EventSettings = {
	styles:       [],
	rules:        [],
	defaultStart: '12:00:00',
};


/*
	The size, spacing and capitalisation of a sub-nav button - what subNavButtonBaseCss() used to
	hard-code, as Style Builder properties. Padding and margins go in as 'opposites' because both were
	two-value css shorthands: '0.44em 2.25em' and '0 0.4em'.

	Shared with seedSubNavButtonSizing.ts, which writes the same four onto every existing venue's style.
*/
export const subNavButtonSizing: CustomStyleProperty[] = [
	{property:'Font size',      params:{size:'15px'}},
	{property:'Text transform', params:{transform:'capitalize'}},
	{property:'Margins',        params:{margin:{sides:'opposites', vertical:'0', horizontal:'0.4em'}}},
	{property:'Padding',        params:{padding:{sides:'opposites', vertical:'0.44em', horizontal:'2.25em'}}},
];


/*
	The Style Builder styles a brand new site starts out with - the two the code draws its buttons
	from, and nothing else. addVenue.ts names them in frontendSettings.buttonStyle / subNavButtonStyle.

	Both are seeded with a single 'self' rule, which is the location that means 'this button' and the
	only one namedStyleCss() reads. 'Standard Button' carries a text colour and nothing more:
	buttonBaseCss() only ever hard-coded that - it has no background or border of its
	own - and it still supplies the size, padding and capitalisation itself. Every venue that wanted a
	filled or bordered button, a corner radius or a different size named it in its own settings class,
	and does it here now.

	'Sub Nav Button' carries the size, spacing and capitalisation that make one of these a sub-nav
	button rather than a call to action. Those were literals inside subNavButton() until
	seedSubNavButtonSizing.ts moved them out; a venue can see and change them in the Style Builder now
	like any other part of the look. Its colours are not here - subNavButton()'s palette-driven fill
	(dim background, text-coloured label) is what the row has always shown, and a style that says
	nothing about a colour leaves that standing.

	A venue adds further styles itself on /admin/style-builder.
*/
export const defaultCustomStyles: CustomStyle[] = [
	{
		name: standardButtonStyleName,
		explanation: 'The look of your buttons.',
		rules: [{location:'self', properties:[{property:'Colour', params:{colour:fromPalette('text')}}]}],
	},
	{
		name: subNavButtonStyleName,
		explanation: "The look of the button rows under a page heading - an event's lineup filters, a menu's section jumps.",
		rules: [{location:'self', properties: subNavButtonSizing}],
	},
];



/*
	The footer settings a brand new site starts out with - an empty style list and no rules. The
	divider and venue-name colours the footer shows out of the box come from BaseFooter.tsx's
	palette-driven default (feature divider, text heading); a venue that wants to pull away turns on a
	standard style or adds a rule under 'Footer' here.
*/
export const defaultFooterSettings: FooterSettings = {
	rules: [],
};


/*
	The text styling a brand new site starts out with: nothing beyond what standardText() already
	gives every field, in the default fonts below: Open Sans for the body, and Oswald for the three
	heading styles. Each field is present as a style so the settings page always has an object to
	write into.
*/
export const defaultTextSettings: TextSettings = {
	bodyFontFamily: '"Open Sans", sans-serif',

	pageTitle: {fontFamily: '"Oswald", sans-serif'},
	title:     {fontFamily: '"Oswald", sans-serif'},
	teaser:    {},
	tagline:   {},
	subtitle:  {fontFamily: '"Oswald", sans-serif'},
	plain:     {},
};


/*
	The specials settings a brand new site starts out with: the single 'General' group BaseSettings
	used to hard-code. A venue adds or removes further groups itself under 'Specials' on
	/admin/settings.
*/
export const defaultSpecialsSettings: SpecialsSettings = {
	groups: {general: 'General'},
};


/* The fonts a brand new site starts out loading - see defaultTextSettings for where they are used. A
   venue imports or uploads further fonts under 'Fonts' on /admin/settings. */
export const defaultVenueFonts: VenueFonts = {
	google: [
		{spec: 'Open+Sans:ital,wght@0,300..800;1,300..800', fallback: 'sans-serif'},
		{spec: 'Oswald:wght@200..700',                      fallback: 'sans-serif'},
	],
	external: [],
	uploaded: [],
};


/* A brand new site starts with a home page and nothing else - it adds the rest itself from the
   backend's Pages menu. Something has to sit at '/', or the site has no front page. */
export const defaultVenuePages: PageSettings[] = [
	{slug: 'home', title: 'Home', home: true, disable: false},
];
